"use client";

import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Fade, FormHelperText } from "@mui/material";
import Link from "next/link";
import { GoogleButton } from "./GoogleButton";
import { useFetchWithRetry } from "src/hooks/useFetch";
import { useSearchParams } from "next/navigation";

export default function LoginComponent() {
  const [csrf, setCsrf] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const csrfToken = "authjs.csrf-token";
  const { data, error: fetchError } = useFetchWithRetry(`/api/cookies`, {
    method: "GET",
    credentials: "include",
  });
  const searchParams = useSearchParams();

  useEffect(() => {
    const errorParam = searchParams.get("error");

    if (errorParam === "CredentialsSignin") {
      setError("E-mail ou senha inválidos");
    } else if (errorParam === "Configuration") {
      setError("Erro de configuração do servidor. Tente novamente mais tarde.");
    } else {
      setError(null);
    }
  }, [searchParams]);
  useEffect(() => {
    if (data && data.cookies && data.cookies[csrfToken]) {
      setCsrf(data.cookies[csrfToken].split("%")[0]);
    }
  }, [data, fetchError]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  return (
    <div className="w-full min-h-screen flex justify-center items-center p-2 md:p-0">
      <Fade in={true} timeout={1000}>
        <div className="flex flex-col bg-white p-8 w-[30rem] border border-slate-400 rounded-md shadow-md">
          <Typography
            component="h1"
            variant="h4"
            sx={{
              width: "100%",
              fontSize: "clamp(1.2rem, 10vw, 1.5rem)",
              mb: 2,
              textAlign: "center",
            }}
            className="font-inter font-semibold"
          >
            Acessando Conta
          </Typography>
          {csrf ? <GoogleButton csrf={csrf} /> : <div>Loading</div>}
          <div className="flex h-full justify-center items-center gap-2 mb-4 mt-4">
            <div className="bg-black w-full h-[1px]" />
            Ou
            <div className="bg-black w-full h-[1px]" />
          </div>
          <Box
            component="form"
            action={`${process.env.NEXT_PUBLIC_WALDEM_API}/api/users/auth/callback/credentials`}
            method="POST"
            noValidate
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: 2,
            }}
          >
            <input type="hidden" name="csrfToken" value={csrf || ""} />
            <input
              type="hidden"
              name="callbackUrl"
              value={`http://localhost:3000`}
            />
            <FormControl>
              <FormLabel className="font-inter" sx={{ mb: 1 }} htmlFor="email">
                Email
              </FormLabel>
              <TextField
                error={Boolean(error)}
                id="email"
                type="email"
                name="email"
                placeholder="exemplo@email.com"
                autoComplete="email"
                required
                size="small"
                variant="outlined"
                className="font-inter"
                color={error ? "error" : "primary"}
                sx={{
                  m: 0,
                  "& .MuiFormHelperText-root": {
                    margin: 0,
                    mt: 1,
                    color: error ? "error.main" : "inherit",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </FormControl>
            <FormControl>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <FormLabel
                  className="font-inter"
                  sx={{ mb: 1 }}
                  htmlFor="password"
                >
                  Senha
                </FormLabel>
                <Link href="/accountrecovery">Esqueceu sua senha?</Link>
              </Box>
              <TextField
                error={Boolean(error)}
                helperText={
                  <FormHelperText
                    component="span"
                    sx={{
                      margin: 0,
                      mt: 1,
                      color: error ? "error.main" : "inherit",
                    }}
                  >
                    {error}
                  </FormHelperText>
                }
                name="password"
                placeholder="••••••"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                required
                fullWidth
                variant="outlined"
                size="small"
                className="font-inter"
                color={error ? "error" : "primary"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  m: 0,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                  "& .MuiFormHelperText-root": {
                    margin: 0,
                    mt: 1,
                    color: error ? "error.main" : "inherit",
                  },
                }}
              />
            </FormControl>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="rounded-lg"
              sx={{
                backgroundColor: "rgb(5, 7, 10)",
                backgroundImage:
                  "linear-gradient(rgb(51, 60, 77), rgb(11, 14, 20))",
                "&:hover": {
                  backgroundColor: "rgb(15, 20, 30)",
                  backgroundImage:
                    "linear-gradient(rgb(71, 80, 97), rgb(31, 34, 40))",
                },
              }}
            >
              Entrar
            </Button>
          </Box>
        </div>
      </Fade>
    </div>
  );
}
